import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { Offer } from '../../../model/offer';
import { SharedService } from '../../../_services/shared.service';
import { ShoppingCartService } from '../../../_services/shopping-cart.service';
import { UiNewOfferPhotoTermsComponent } from '../ui-new-offer-photo-terms/ui-new-offer-photo-terms.component';
import { LxResponsiveClassDirective } from '../../../directives/lx-responsive-class.directive';

@Component({
  selector: 'ui-new-offer-photo-gallery',
  standalone: true,
  imports: [CommonModule, LxResponsiveClassDirective],
  templateUrl: './ui-new-offer-photo-gallery.component.html',
  styleUrl: './ui-new-offer-photo-gallery.component.scss',
})
export class UiNewOfferPhotoGalleryComponent implements OnInit {
  readonly arrowResponsiveClass = {
    horizontal: 'carousel-btn-visible',
    vertical: 'carousel-btn-hidden',
  } as const;

  @Input() offer!: Offer | null;
  @Input() index = 0;
  @Input() offerAliasId = '';
  @Input() propertyId = '';

  images: string[] = [];
  currentImageIndex = 0;
  private pointerStartX: number | null = null;
  private pointerStartY: number | null = null;
  private isPointerDragging = false;
  private readonly swipeThresholdPx = 40;

  constructor(
    public modalRef: BsModalRef,
    public sharedService: SharedService,
    public shoppingCartService: ShoppingCartService,
  ) {}

  ngOnInit(): void {
    this.initImages();
  }

  private initImages(): void {
    const amen = this.offer?.amenities;
    const urls = (amen?.room_photo_urls ?? []).filter(Boolean);

    if (urls.length > 0) {
      this.images = urls.map(u => encodeURI(u));
      this.currentImageIndex = 0;
      return;
    }

    const single = amen?.room_photo_url;
    this.images = single ? [encodeURI(single)] : [];
    this.currentImageIndex = 0;
  }

  get hasImages(): boolean {
    return this.images.length > 0;
  }

  get currentImageUrl(): string {
    if (!this.images.length) return '';
    return this.images[this.currentImageIndex] ?? this.images[0] ?? '';
  }

  nextImage(ev?: Event): void {
    if (ev) ev.stopPropagation();
    if (this.images.length <= 1) return;
    this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
  }

  prevImage(ev?: Event): void {
    if (ev) ev.stopPropagation();
    if (this.images.length <= 1) return;
    this.currentImageIndex = (this.currentImageIndex - 1 + this.images.length) % this.images.length;
  }

  goTo(index: number, ev?: Event): void {
    if (ev) ev.stopPropagation();
    if (index < 0 || index >= this.images.length) return;
    this.currentImageIndex = index;
  }

  onPointerDown(event: PointerEvent): void {
    if (this.images.length <= 1) return;

    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.isPointerDragging = true;
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.isPointerDragging || this.pointerStartX == null || this.pointerStartY == null) {
      this.resetPointerGesture();
      return;
    }

    const deltaX = event.clientX - this.pointerStartX;
    const deltaY = event.clientY - this.pointerStartY;

    if (Math.abs(deltaX) >= this.swipeThresholdPx && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        this.nextImage(event);
      } else {
        this.prevImage(event);
      }
    }

    this.resetPointerGesture();
  }

  onPointerCancel(): void {
    this.resetPointerGesture();
  }

  private resetPointerGesture(): void {
    this.pointerStartX = null;
    this.pointerStartY = null;
    this.isPointerDragging = false;
  }

  shouldShowStrike(): boolean {
    const offer = this.offer;
    if (!offer || offer.bar_price == null || offer.ofr_price == null) return false;

    const bar = Number(offer.bar_price);
    const ofr = Number(offer.ofr_price);
    if (!isFinite(bar) || bar <= 0 || !isFinite(ofr)) return false;

    const discountPercent = ((bar - ofr) / bar) * 100;
    return discountPercent >= 5;
  }

  checkIfOfferInCart(): boolean {
    return this.shoppingCartService.checkIfOfferInCart(
      this.offerAliasId,
      this.offer?.offer_set_id || '',
      this.offer?.offer_id || '',
    );
  }

  addOfferToCart(): void {
    if (!this.offer) return;
    this.shoppingCartService.addOfferToCart(this.offer, this.offerAliasId, true);
    this.closeModal();
  }

  removeOfferFromCart(): void {
    if (!this.offer) return;
    this.shoppingCartService.removeOfferFromCart(this.offer, this.offerAliasId);
    this.closeModal();
  }

  requestUpgrade(): void {
    if (!this.offer) return;
    this.shoppingCartService.addOfferToCart(this.offer, this.offerAliasId, false);
    this.shoppingCartService.openCheckOutOfferTermsModal(
      this.offerAliasId,
      this.propertyId,
      UiNewOfferPhotoTermsComponent,
    );
    this.closeModal();
  }

  onPrimaryAction(): void {
    if (this.sharedService.requestOnlyMode) {
      this.requestUpgrade();
      return;
    }

    if (this.checkIfOfferInCart()) {
      this.removeOfferFromCart();
      return;
    }

    this.addOfferToCart();
  }

  get primaryActionLabel(): string {
    if (this.sharedService.requestOnlyMode) return 'Request Upgrade';
    if (this.checkIfOfferInCart()) return 'Remove';
    return 'Add to Cart';
  }

  get primaryActionIcon(): string {
    if (this.sharedService.requestOnlyMode) return 'upgrade';
    if (this.checkIfOfferInCart()) return 'remove_shopping_cart';
    return 'add_shopping_cart';
  }

  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.hide();
    }
  }
}
